import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { fmtDate, fmtRelative, PRIORITY_LABELS } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Mail, Phone, MapPin, Calendar, Tag, FileText, MessageSquarePlus, Trash2, Image as ImageIcon, Wrench, AlertOctagon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import ServiceShipmentsCard from "./ServiceShipmentsCard";

const ACTION_LABELS = {
  "service.created": "Cerere creată",
  "service.update": "Modificată",
  "service.status_change": "Status schimbat",
  "service.note_added": "Notă internă",
  "service.delete": "Ștearsă",
};

const WHEN_LABELS = {
  from_unboxing: "Din momentul despachetării",
  first_use: "La prima utilizare",
  few_days: "După câteva zile",
  few_weeks: "După câteva săptămâni",
  months_later: "După câteva luni",
  other: "Alt moment",
};

export default function ServiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();
  const [item, setItem] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noteBody, setNoteBody] = useState("");
  const [techNotes, setTechNotes] = useState("");
  const [pickupZip, setPickupZip] = useState("");

  const load = async () => {
    try {
      const [r, s] = await Promise.all([api.get(`/service/${id}`), api.get("/service/statuses")]);
      setItem(r.data);
      setStatuses(s.data);
      setTechNotes(r.data.technician_notes || "");
      setPickupZip(r.data.pickup?.zip || "");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  const updateField = async (patch) => {
    try {
      await api.patch(`/service/${id}`, patch);
      toast.success("Salvat");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addNote = async () => {
    if (!noteBody.trim()) return;
    try {
      await api.post(`/service/${id}/notes`, { body: noteBody });
      setNoteBody("");
      toast.success("Notă adăugată");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteNote = async (noteId) => {
    try {
      await api.delete(`/service/${id}/notes/${noteId}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteService = async () => {
    try {
      await api.delete(`/service/${id}`);
      toast.success("Cerere service ștearsă");
      navigate("/service");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return (
    <div className="space-y-6">
      <Skeleton className="h-10 w-64" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Skeleton className="h-96 lg:col-span-2" />
        <Skeleton className="h-96" />
      </div>
    </div>
  );

  if (!item) return <div className="text-center text-muted-foreground py-12">Cerere inexistentă.</div>;

  const status = item.status;
  const pt = item.problem_type;
  const totalItems = item.items?.reduce((a, b) => a + (b.quantity || 0), 0);

  return (
    <div className="space-y-6" data-testid="service-detail-page">
      <Link to="/service" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5">
        <ArrowLeft className="h-3.5 w-3.5" /> Înapoi la cereri service
      </Link>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="font-mono text-xs text-muted-foreground">{item.service_number}</div>
            {status && (
              <Badge style={{ color: status.color, background: status.color + "15", borderColor: status.color + "30" }}>
                {status.name}
              </Badge>
            )}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{item.customer_name}</h1>
          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
            <span className="flex items-center gap-1.5"><Tag className="h-3.5 w-3.5" />#{item.order_number}</span>
            <span className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />{fmtDate(item.created_at)}</span>
            <span>· {totalItems} produs(e)</span>
            <span className="flex items-center gap-1.5"><Wrench className="h-3.5 w-3.5" />{pt?.name}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Select value={item.status_id} onValueChange={(v) => updateField({ status_id: v })} disabled={!hasPermission("edit_service")}>
            <SelectTrigger className="w-[180px]" data-testid="status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {statuses.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  <span className="inline-flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
                    {s.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={item.priority} onValueChange={(v) => updateField({ priority: v })} disabled={!hasPermission("edit_service")}>
            <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(PRIORITY_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
            </SelectContent>
          </Select>
          {hasPermission("delete_service") && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-9 text-red-600 border-red-500/40 hover:bg-red-500/10"
                  data-testid="service-delete-btn"
                  title="Șterge cererea de service"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="service-delete-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Ștergi cererea de service?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Această acțiune este permanentă. Cererea <span className="font-mono font-semibold">{item.service_number}</span> de la <strong>{item.customer_name}</strong> va fi eliminată definitiv, împreună cu notele și atașamentele asociate.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="service-delete-cancel">Anulează</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={deleteService}
                    className="bg-red-600 hover:bg-red-700"
                    data-testid="service-delete-confirm"
                  >
                    Șterge definitiv
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Defect details */}
          <Card className="p-6 bg-card border-border">
            <h3 className="font-display font-semibold text-lg mb-4 flex items-center gap-2"><AlertOctagon className="h-4 w-4 text-red-500" /> Defect raportat</h3>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-widest font-semibold mb-1">Tip problemă</div>
                  <div className="font-medium">{pt?.name || "—"}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-widest font-semibold mb-1">Când a apărut</div>
                  <div className="font-medium">{WHEN_LABELS[item.when_started] || "—"}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-widest font-semibold mb-1">Produs utilizat</div>
                  <div className="font-medium">{item.product_used === true ? "Da" : item.product_used === false ? "Nu" : "—"}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-widest font-semibold mb-1">Deteriorare vizuală</div>
                  <div className="font-medium">{item.visual_damage === true ? "Da" : item.visual_damage === false ? "Nu" : "—"}</div>
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-widest font-semibold mb-1 mt-2">Descriere</div>
                <p className="text-sm leading-relaxed">{item.defect_description}</p>
              </div>
              {item.customer_notes && (
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-widest font-semibold mb-1 mt-2">Observații client</div>
                  <p className="text-sm leading-relaxed text-muted-foreground">{item.customer_notes}</p>
                </div>
              )}
            </div>
          </Card>

          {/* Products */}
          <Card className="p-6 bg-card border-border">
            <h3 className="font-display font-semibold text-lg mb-4">Produse afectate</h3>
            <div className="divide-y divide-border">
              {item.items?.map((it) => (
                <div key={it.line_item_id} className="flex items-center gap-4 py-3 first:pt-0 last:pb-0">
                  {it.image ? (
                    <img src={it.image} alt={it.title} className="h-14 w-14 rounded-md object-cover bg-secondary" />
                  ) : (
                    <div className="h-14 w-14 rounded-md bg-secondary flex items-center justify-center"><ImageIcon className="h-5 w-5" /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{it.title}</div>
                    <div className="text-xs text-muted-foreground">{it.variant_title}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-sm">×{it.quantity}</div>
                    <div className="text-xs text-muted-foreground font-mono">{it.price} RON</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Files */}
          {item.files?.length > 0 && (
            <Card className="p-6 bg-card border-border">
              <h3 className="font-display font-semibold text-lg mb-4">Atașamente client ({item.files.length})</h3>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                {item.files.map((f) => {
                  const isVideo = f.content_type?.startsWith("video/");
                  const src = `${API_BASE}/files/${f.id}`;
                  return (
                    <a key={f.id} href={src} target="_blank" rel="noopener noreferrer" className="aspect-square rounded-md overflow-hidden bg-secondary border border-border block group">
                      {isVideo ? (
                        <video src={src} className="w-full h-full object-cover" muted />
                      ) : (
                        <img src={src} alt={f.original_filename} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                      )}
                    </a>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Technician notes */}
          <Card className="p-6 bg-card border-border">
            <h3 className="font-display font-semibold text-lg mb-4">Note tehnician (interne)</h3>
            <Textarea value={techNotes} onChange={(e) => setTechNotes(e.target.value)} placeholder="Diagnoză, piese de înlocuit, observații tehnice…" rows={3} className="mb-2" data-testid="tech-notes" disabled={!hasPermission("edit_service")} />
            {hasPermission("edit_service") && (
              <Button size="sm" variant="secondary" onClick={() => updateField({ technician_notes: techNotes })} data-testid="save-tech-notes">
                Salvează
              </Button>
            )}
          </Card>

          {/* Internal notes thread */}
          <Card className="p-6 bg-card border-border">
            <h3 className="font-display font-semibold text-lg mb-4">Note interne</h3>
            {hasPermission("edit_service") && (
              <div className="flex gap-2 mb-4">
                <Textarea placeholder="Adaugă o notă internă…" value={noteBody} onChange={(e) => setNoteBody(e.target.value)} rows={2} data-testid="note-input" />
                <Button onClick={addNote} disabled={!noteBody.trim()} data-testid="add-note-btn"><MessageSquarePlus className="h-4 w-4" /></Button>
              </div>
            )}
            {item.notes?.length === 0 && <div className="text-sm text-muted-foreground text-center py-6">Nicio notă încă.</div>}
            <div className="space-y-3">
              {item.notes?.map((n) => (
                <div key={n.id} className="flex gap-3 group">
                  <Avatar className="h-7 w-7">
                    <AvatarFallback className="bg-primary/10 text-primary text-[10px] font-semibold">
                      {(n.author_name || "?").split(" ").map((p) => p[0]).join("").slice(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{n.author_name}</span>
                      <span className="text-xs text-muted-foreground">{fmtRelative(n.created_at)}</span>
                    </div>
                    <p className="text-sm mt-1 whitespace-pre-wrap">{n.body}</p>
                  </div>
                  {n.author_id === user?.id && (
                    <button onClick={() => deleteNote(n.id)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-500 transition-all">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* RIGHT 1/3 */}
        <div className="space-y-6">
          <Card className="p-6 bg-card border-border">
            <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-4">Date client</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-muted-foreground" /><span className="font-mono text-xs">{item.email}</span></div>
              <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-muted-foreground" /><span className="font-mono text-xs">{item.phone}</span></div>
            </div>
          </Card>

          {/* Pickup */}
          {item.pickup && (
            <Card className="p-6 bg-card border-border">
              <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-4 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> Ridicare produs</h3>
              <div className="text-sm space-y-1.5">
                <div>{item.pickup.address1}</div>
                <div className="text-muted-foreground text-xs">{item.pickup.city}{item.pickup.county ? ` · ${item.pickup.county}` : ""}</div>
                {hasPermission("edit_service") ? (
                  <div className="flex items-center gap-1.5 pt-1">
                    <Input
                      placeholder="Cod poștal"
                      value={pickupZip}
                      onChange={(e) => setPickupZip(e.target.value)}
                      className="h-8 w-28 font-mono text-xs"
                      data-testid="pickup-zip-input"
                    />
                    {pickupZip !== (item.pickup.zip || "") && (
                      <Button size="sm" variant="secondary" className="h-8" onClick={() => updateField({ pickup: { zip: pickupZip } })} data-testid="pickup-zip-save">
                        Salvează
                      </Button>
                    )}
                  </div>
                ) : (
                  item.pickup.zip && <div className="text-muted-foreground text-xs font-mono">{item.pickup.zip}</div>
                )}
                {!item.pickup.zip && (
                  <div className="text-[11px] text-amber-600">Cod poștal lipsă — necesar pentru generarea AWB-ului GLS.</div>
                )}
                <div className="pt-2 border-t border-border mt-2">
                  <div className="font-medium">{item.pickup.contact_name}</div>
                  <div className="text-xs text-muted-foreground font-mono">{item.pickup.contact_phone}</div>
                </div>
                {item.pickup.preferred_slot && (
                  <div className="text-xs text-muted-foreground pt-2">
                    Interval preferat: <span className="text-foreground">{item.pickup.preferred_slot}</span>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Dual-direction GLS logistics (pickup + repaired product shipping) */}
          <ServiceShipmentsCard serviceId={id} onAfterChange={load} />

          {/* Timeline */}
          <Card className="p-6 bg-card border-border">
            <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-4 flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> Istoric</h3>
            <div className="space-y-3">
              {item.timeline?.length === 0 && <div className="text-xs text-muted-foreground">Niciun eveniment.</div>}
              {item.timeline?.map((t) => (
                <div key={t.id} className="flex gap-3">
                  <div className="relative">
                    <div className="h-2 w-2 rounded-full bg-primary mt-1.5" />
                    <div className="absolute top-3 left-1/2 -translate-x-1/2 w-px h-full bg-border" />
                  </div>
                  <div className="flex-1 pb-3">
                    <div className="text-xs font-medium">{ACTION_LABELS[t.action] || t.action}</div>
                    <div className="text-xs text-muted-foreground">{t.actor_name || "Sistem"} · {fmtRelative(t.created_at)}</div>
                    {t.details?.from && t.details?.to && (
                      <div className="text-xs text-muted-foreground font-mono mt-0.5">{t.details.from} → {t.details.to}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
